import { FinBoxResponse } from "./APIResponse";
import { AuthService } from "../../services/apis/authService";
import { FINBOX_USER, FINBOX_USER_PERMISSIONS } from "../context/AuthContext";
import LZString from "lz-string";
import { authService } from "../../services/apis";
export const getPermissionsFromStorage = () => {
  let value = "";
  try {
    value =
      LZString.decompress(
        localStorage.getItem(FINBOX_USER_PERMISSIONS) ?? ""
      ) || "";
    if (value) {
      value = JSON.parse(value);
      value = value.permissions;
    }
  } catch (error) {
    console.error(error);
  }
  return value;
};

export const getLenderUserInfoFromLocalStorage = () => {
  return JSON.parse(
    localStorage.getItem("finbox-lender-dashboard-user") || "{}"
  );
};

export const getUserInfoFromLocalStorage = () =>
  JSON.parse(
    LZString.decompress(localStorage.getItem("finbox-dashboard-user") ?? "") ||
      "{}"
  );

const forbiddenRoutes = [
  "signin",
  "reset-password",
  "verify-account-details",
  "/verify-mobile-password",
  "/verify-otp",
  "/authenticate",
  "/password-expire",
  "/signin-mobile"
];

function handleInvalidTokenError() {
  console.log("handle invalid token error");
  localStorage.clear();
  // abortController.abort();

  const currentRoute = window.location.href;
  const shouldRedirectToSignIn = !forbiddenRoutes.some(
    (route) => currentRoute.indexOf(route) !== -1
  );
  if (shouldRedirectToSignIn) {
    window.location.href = "/signin";
  }
}

export const Post = async <T extends any>(
  path: string,
  token: string,
  data: Record<string, any>,
  headers?: Record<string, any>,
  multipart?: boolean,
  apiKey?: string,
  requestOptionsPayload?: any
): Promise<FinBoxResponse<T> & { statusCode: number }> => {
  const myHeaders = new Headers();
  let shouldStringify = false;

  if (!multipart) {
    myHeaders.append("Content-Type", "application/json");
    shouldStringify = true;
  }

  headers?.forEach((header, key) => {
    myHeaders.append(key, header);

    const isApplicationJSON =
      key.toLowerCase() === "content-type" &&
      headers.get(key).toLowerCase() === "application/json";
    if (isApplicationJSON) {
      shouldStringify = true;
    }
  });

  if (token) myHeaders.append("token", token);
  const bearerToken = authService.getBearerToken();
  if (bearerToken) myHeaders.append("Authorization", `Bearer ${bearerToken}`);

  const requestOptions: any = {
    method: "POST",
    headers: myHeaders,
    body: shouldStringify ? JSON.stringify(data) : data,
    redirect: "follow",
    ...(requestOptionsPayload || {})
  };

  let json: FinBoxResponse<T> & { statusCode: number };
  try {
    const url = `${import.meta.env.REACT_APP_API_ENDPOINT}${path}`;
    const response = await fetch(url, requestOptions);

    json = await response.json();
    json["statusCode"] = response.status;
    if (response.status == 401) {
      handleInvalidTokenError();
    }
  } catch (error) {
    json = {
      data: null,
      status: false,
      error: "something went wrong",
      statusCode: 500
    };
  }
  return json;
};

export const Put = async <T extends any>(
  path: string,
  token: string,
  data: Record<string, any>,
  headers?: Record<string, any>,
  multipart?: boolean
): Promise<FinBoxResponse<T> & { statusCode: number }> => {
  const myHeaders = new Headers();
  let shouldStringify = false;

  if (!multipart) {
    myHeaders.append("Content-Type", "application/json");
    shouldStringify = true;
  }
  headers?.forEach((header, key) => {
    myHeaders.append(key, header);

    const isApplicationJSON =
      key.toLowerCase() === "content-type" &&
      headers.get(key).toLowerCase() === "application/json";
    if (isApplicationJSON) {
      shouldStringify = true;
    }
  });

  if (token) myHeaders.append("token", token);
  const bearerToken = authService.getBearerToken();
  if (bearerToken) myHeaders.append("Authorization", `Bearer ${bearerToken}`);

  const requestOptions: any = {
    method: "PUT",
    headers: myHeaders,
    body: shouldStringify ? JSON.stringify(data) : data,
    redirect: "follow"
  };

  let json: FinBoxResponse<T> & { statusCode: number };
  try {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_ENDPOINT}${path}`,
      requestOptions
    );
    json = await response.json();
    json["statusCode"] = response.status;
    if (response.status == 401) {
      handleInvalidTokenError();
    }
  } catch (error) {
    json = {
      data: null,
      status: false,
      error: "something went wrong",
      statusCode: 500
    };
  }
  return json;
};

export const Get = async <T extends any>(
  path: string,
  token: string,
  headers?: Record<string, any>,
  isRedGateApi?: boolean
): Promise<FinBoxResponse<T> & { statusCode: number }> => {
  const myHeaders = new Headers();
  if (!headers?.has("Content-Type")) {
    myHeaders.append("Content-Type", "application/json");
  }
  if (token) myHeaders.append("token", token);
  const bearerToken = authService.getBearerToken();
  if (bearerToken) myHeaders.append("Authorization", `Bearer ${bearerToken}`);
  if (headers)
    headers.forEach((header: any, key: any) => myHeaders.append(key, header));

  const requestOptions: RequestInit = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow"
  };
  let json: FinBoxResponse<T> & { statusCode: number };
  try {
    let url = `${import.meta.env.REACT_APP_API_ENDPOINT}${path}`;
    if (isRedGateApi) {
      url = `${import.meta.env.REACT_APP_REDGATE_API_ENDPOINT}${path}`;
    }
    const response = await fetch(url, requestOptions);
    if (myHeaders.get("Content-Type") !== "text/html") {
      json = await response.json();
      json["statusCode"] = response.status;
    } else {
      json = {
        data: await response.text(),
        status: true
      };
    }
    if (response.status == 401) {
      handleInvalidTokenError();
    }
  } catch (error) {
    json = {
      data: null,
      status: false,
      error: "something went wrong",
      statusCode: 500
    };
  }
  return json;
};

export const get = async <T extends any>(
  path: string,
  pageName: string
): Promise<FinBoxResponse<T> & { statusCode: number }> => {
  const token = authService.getToken();
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");
  if (token) myHeaders.append("token", token);
  const bearerToken = authService.getBearerToken();
  if (bearerToken) myHeaders.append("Authorization", `Bearer ${bearerToken}`);
  if (pageName) myHeaders.append("page-header", pageName);

  const requestOptions: RequestInit = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow"
  };
  let json: FinBoxResponse<T> & { statusCode: number };
  try {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_ENDPOINT}${path}`,
      requestOptions
    );
    json = await response.json();
    json["statusCode"] = response.status;
    if (response.status == 401 || json.statusCode == 401) {
      handleInvalidTokenError();
    }
  } catch (error) {
    json = {
      data: null,
      status: false,
      error: "something went wrong",
      statusCode: 500
    };
  }
  return json;
};
export const Delete = async <T extends any>(
  path: string,
  token: string,
  body?: any,
  headers?: Record<string, any>
): Promise<FinBoxResponse<T> & { statusCode: number }> => {
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");
  if (token) myHeaders.append("token", token);
  const bearerToken = authService.getBearerToken();
  if (bearerToken) myHeaders.append("Authorization", `Bearer ${bearerToken}`);

  if (headers)
    headers.forEach((header: any, key: any) => myHeaders.append(key, header));

  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: myHeaders,
    redirect: "follow"
  };
  if (typeof body !== "undefined" && Object?.keys(body)?.length) {
    requestOptions["body"] = JSON.stringify(body);
  }
  let json: FinBoxResponse<T> & { statusCode: number };
  try {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_ENDPOINT}${path}`,
      requestOptions
    );
    json = await response.json();
    json["statusCode"] = response.status;
    if (response.status == 401) {
      handleInvalidTokenError();
    }
  } catch (error) {
    json = {
      data: null,
      status: false,
      error: "something went wrong",
      statusCode: 500
    };
  }
  return json;
};

export function composedLoader(loader: any, pageName?: string) {
  const getToken = () => (window as any).token || authService.getToken();
  return (args: any) => {
    const getPermissions = () =>
      (window as any).permissions || getPermissionsFromStorage();
    if (!pageName) {
      throw "No page name set";
    }
    const permissions = getPermissions();
    if (permissions[pageName!]) {
      return loader({ ...args, getToken, pageName, getPermissions });
    } else {
      return function () {
        return {};
      };
    }
  };
}

export const Patch = async <T extends any>(
  path: string,
  token: string,
  data: Record<string, any>,
  headers?: Record<string, any>,
  multipart?: boolean
): Promise<FinBoxResponse<T> & { statusCode: number }> => {
  const myHeaders = new Headers();
  let shouldStringify = false;
  if (!multipart) {
    myHeaders.append("Content-Type", "application/json");
    shouldStringify = true;
  }
  headers?.forEach((header, key) => {
    myHeaders.append(key, header);

    const isApplicationJSON =
      key.toLowerCase() === "content-type" &&
      headers.get(key).toLowerCase() === "application/json";
    if (isApplicationJSON) {
      shouldStringify = true;
    }
  });

  if (token) myHeaders.append("token", token);

  const requestOptions: any = {
    method: "PATCH",
    headers: myHeaders,
    body: shouldStringify ? JSON.stringify(data) : data,
    redirect: "follow"
  };

  let json: FinBoxResponse<T> & { statusCode: number };
  try {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_ENDPOINT}${path}`,
      requestOptions
    );
    json = await response.json();
    json["statusCode"] = response.status;

    if (response.status == 401 || json.statusCode == 401) {
      handleInvalidTokenError();
    }
  } catch (error) {
    json = {
      data: null,
      status: false,
      error: "something went wrong",
      statusCode: 500
    };
  }
  return json;
};
